import { useState, useEffect, useRef } from "react";
import axios from "../customAxios.js";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
  CircularProgress,
  Box,
  Button,
  Switch,
  FormControlLabel,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import es from "date-fns/locale/es";
import { dateToDatabase, mapStatusDashboard } from "../utils/functions";
import { useAuth } from "../hooks/useAuth.jsx";

function Dashboard(props) {
  //values to control pagination of the component table
  const [page, setPage] = useState(
    localStorage.getItem("deliveriesFilter")
      ? JSON.parse(localStorage.getItem("deliveriesFilter")).page
      : 1,
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    localStorage.getItem("deliveriesFilter")
      ? JSON.parse(localStorage.getItem("deliveriesFilter")).rowsPerPage
      : 20,
  );
  //data that will be set on every page change and filter set
  const [loadingPageData, setLoadingPageData] = useState(true);

  // const [loading, setLoading] = useState(false);
  const daysOffset = props.daysOffset;
  //filter values of the table
  const [startDate, setStartDate] = useState(
    localStorage.getItem("deliveriesFilter")
      ? new Date(JSON.parse(localStorage.getItem("deliveriesFilter")).startDate)
      : new Date(new Date().setDate(new Date().getDate() - daysOffset)),
  );
  const [endDate, setEndDate] = useState(
    localStorage.getItem("deliveriesFilter")
      ? new Date(JSON.parse(localStorage.getItem("deliveriesFilter")).endDate)
      : new Date(),
  );
  const [statusFilter, setStatusFilter] = useState(
    localStorage.getItem("deliveriesFilter")
      ? JSON.parse(localStorage.getItem("deliveriesFilter")).statusFilter
      : "all",
  );
  const [search, setSearch] = useState(
    localStorage.getItem("deliveriesFilter")
      ? JSON.parse(localStorage.getItem("deliveriesFilter")).search
      : "",
  );

  const [assignedDriverFilter, setAssignedDriverFilter] = useState(
    localStorage.getItem("deliveriesFilter")
      ? JSON.parse(localStorage.getItem("deliveriesFilter"))
          .assignedDriverFilter
      : "",
  );

  //aux values for delay on text search fields
  const [auxSearcher, setAuxSearcher] = useState(
    localStorage.getItem("deliveriesFilter")
      ? JSON.parse(localStorage.getItem("deliveriesFilter")).search
      : "",
  );
  const [deliveryStatus, setDeliveryStatus] = useState([]);
  // const [dateRefresh, setDateRefresh] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [errorOnRetrieveData, setErrorOnRetrieveData] = useState();
  const [isInitialRender, setIsInitialRender] = useState(true);
  //useState to catch when general data is already fetched
  const [generalParamsFetched, setGeneralParamsFetched] = useState(false);
  //useState to count the number of deliveries filtered by backend
  const [deliveriesCount, setDeliveriesCount] = useState(0);
  const [deliveriesCountResume, setDeliveriesCountResume] = useState(0);

  // status counters:
  const [counterLabels, setCounterLabels] = useState();
  const [counters, setCounters] = useState({});
  const [courierCount, setCourierCount] = useState([]);
  const [withoutTrackingNumber, setWithoutTrackingNumber] = useState(0);
  // timer
  const [currentTime, setCurrentTime] = useState({ date: "", time: "" });
  //toggle fetch delivery
  const [checked, setChecked] = useState(false); // State to track if the switch is toggled
  const [progress, setProgress] = useState(0); // State for progress bar
  const fetchIntervalRef = useRef(null); // Reference to store the interval for fetchDeliveries
  const progressIntervalRef = useRef(null); // Reference to store the interval for the progress bar
  const autoReloadTimer = 60000;
  const progressTimer = 59;
  // get admin
  const { user } = useAuth();
  const isAdmin = user.user_type.includes("admin");
  const isSeller = user.user_type.includes("seller");

  const [companyFilter, setCompanyFilter] = useState(
    localStorage.getItem("deliveriesFilter")
      ? JSON.parse(localStorage.getItem("deliveriesFilter")).companyFilter
      : isAdmin
        ? "all"
        : null,
  );

  //define url to call on backend depending on user type
  let user_type_url_path = "api/dashboard/";

  useEffect(() => {
    //if localStorage "deliveriesFilter" does not exist, set page to 0
    if (!localStorage.getItem("deliveriesFilter")) {
      setPage(1);
    }
    //if is on useEffect not on initial render, set page 0
    if (isInitialRender) {
      setIsInitialRender(false);
    } else {
      setPage(1);
    }
  }, [
    startDate,
    endDate,
    statusFilter,
    search,
    rowsPerPage,
    assignedDriverFilter,
  ]);

  useEffect(() => {
    fetchDeliveries();
    //save on localStorage the filter and page values of Deliveries
    localStorage.setItem(
      "deliveriesFilter",
      JSON.stringify({
        startDate,
        endDate,
        statusFilter,
        search,
        page,
        rowsPerPage,
        companyFilter,
        assignedDriverFilter,
      }),
    );
  }, [
    startDate,
    endDate,
    statusFilter,
    search,
    rowsPerPage,
    page,
    companyFilter,
    assignedDriverFilter,
  ]);

  //useEffect to catch auxSearcher changes, and triger a function every 2 seconds that dont change
  useEffect(() => {
    // Delayed search function
    const delayedSearch = (value) => {
      // Your search logic goes here
      setSearch(value);
    };
    if (!loadingPageData) {
      setLoadingPageData(true);
    }
    // Timer variable for setTimeout
    let timer;

    // Set a delay of 2 seconds before executing the search
    const delay = 1500;

    // Clear the previous timer when auxSearcher changes
    clearTimeout(timer);

    // Set a new timer for delayed search
    timer = setTimeout(() => {
      delayedSearch(auxSearcher);
    }, delay);

    // Clean up the timer on component unmount or when auxSearcher changes
    return () => {
      clearTimeout(timer);
    };
  }, [auxSearcher]);

  useEffect(() => {
    fetchDeliveryFilters();
  }, []);

  useEffect(() => {}, [deliveryStatus, companies]); // This will log deliveryStatus every time it updates

  useEffect(() => {
    console.log("courierCount: ", courierCount);
  }, [courierCount]); // This will log deliveryStatus every time it updates

  // toggle: Clear intervals when the component unmounts to avoid memory leaks
  useEffect(() => {
    return () => stopTimers(); // Clean up on unmount
  }, []);

  // Update time every second
  useEffect(() => {
    const timerId = setInterval(updateTime, 1000);
    return () => clearInterval(timerId); // Clean up the timer
  }, []);

  //function to fetch the page data depending on filters, and pagination params
  async function fetchDeliveries() {
    if (!loadingPageData) {
      setLoadingPageData(true);
    }

    const search_params_fetch_orders = {
      start_date: dateToDatabase(startDate),
      end_date: dateToDatabase(endDate),
      company: companyFilter,
      search: search,
      delivery_status: null, // avoid status filter in dashboard
      page: page,
      rowsPerPage: rowsPerPage,
      assignedDriverFilter: assignedDriverFilter,
    };

    try {
      const {
        data: {
          total_deliveries_dashboard,
          status_count_dashboard,
          courier_count_dashboard,
          without_tracking_number_dashboard,
        },
      } = await axios.get(user_type_url_path, {
        params: search_params_fetch_orders,
      });
      const formatted_total_deliveries =
        total_deliveries_dashboard.toLocaleString("de-DE");
      setDeliveriesCount(formatted_total_deliveries);
      setDeliveriesCountResume(total_deliveries_dashboard);
      setCounters(status_count_dashboard);
      setCourierCount(courier_count_dashboard);
      const formatted_without_tracking =
        without_tracking_number_dashboard.toLocaleString("de-DE");
      setWithoutTrackingNumber(formatted_without_tracking);
      setErrorOnRetrieveData();
    } catch (error) {
      console.error(`Ocurrió un error al obtener los deliveries: ${error}`);
      setErrorOnRetrieveData("Ocurrió un error al obtener los deliveries");
    } finally {
      setLoadingPageData(false);
    }
  }

  //function to fetch all general parameters that do not depends on the page and rowsPerPage
  async function fetchDeliveryFilters() {
    try {
      const {
        data: { delivery_statuses, companies },
      } = await axios.get(`delivery_params/${user_type_url_path}`);
      console.log("delivery_statuses es ", delivery_statuses);
      setDeliveryStatus(delivery_statuses);
      setCounterLabels(delivery_statuses);
      isAdmin ? setCompanies(companies) : setCompanies([]);
      setErrorOnRetrieveData();
      setGeneralParamsFetched(true);
    } catch (error) {
      console.error(
        `Ocurrió un error al obtener los contadores y opciones de filtro: ${error}`,
      );
      setErrorOnRetrieveData(
        "Ocurrió un error al obtener los contadores y opciones de filtro",
      );
    }
  }

  // Function to update the current time in Chile timezone
  const updateTime = () => {
    const now = new Date();
    const chileTime = new Date(
      now.toLocaleString("en-US", { timeZone: "America/Santiago" }),
    );

    // Format date (dd-mm-yyyy)
    const formattedDate = chileTime.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    // Format time (hh:mm:ss)
    const formattedTime = chileTime.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    setCurrentTime({ date: formattedDate, time: formattedTime });
  };

  // Function to handle toggle
  const handleToggle = () => {
    setChecked(!checked);

    if (!checked) {
      // If turning ON, start the fetch and progress intervals
      fetchDeliveries(); // Trigger fetchDeliveries immediately
      startFetchTimer();
      startProgressBar();
    } else {
      // If turning OFF, clear both intervals and reset progress
      stopTimers();
    }
  };

  // Function to start fetch timer every 60 seconds
  const startFetchTimer = () => {
    fetchIntervalRef.current = setInterval(() => {
      fetchDeliveries(); // Call fetchDeliveries every 60 seconds
    }, autoReloadTimer); // 60 seconds
  };

  // Function to stop both fetch and progress timers
  const stopTimers = () => {
    clearInterval(fetchIntervalRef.current); // Clear fetch timer
    clearInterval(progressIntervalRef.current); // Clear progress timer
    setProgress(0); // Reset progress
  };

  // Function to start the progress bar (progresses over 60 seconds)
  const startProgressBar = () => {
    setProgress(progressTimer); // Start the progress at 60 seconds
    progressIntervalRef.current = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress <= 0) {
          return progressTimer; // Reset to 60 seconds after reaching 0
        }
        return oldProgress - 1; // Decrease by 1 second every interval
      });
    }, 1000); // Update progress every second
  };

  if (generalParamsFetched) {
    return (
      <>
        <Grid item xs={12}>
          <h2>Dashboard</h2>
        </Grid>
        {/* Combined Filters and Heading Section */}
        <Grid
          container
          spacing={3}
          className="filters-and-timer-container"
          sx={{ mb: 4 }}
        >
          {/* Filters Section */}
          <Grid item xs={12} md={12}>
            <Grid container spacing={isSeller ? 2 : 4}>
              {isSeller && (
                <Grid container className="input-service-filter">
                  <Grid
                    item
                    xs={12}
                    md={6}
                    xl={3}
                    sm="auto"
                    className="input-date-filter"
                  >
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={es}
                    >
                      <DatePicker
                        label="Fecha de inicio"
                        value={startDate}
                        onChange={(newValue) => {
                          if (endDate < newValue) {
                            let newEndDate = new Date(newValue);
                            newEndDate.setDate(
                              newEndDate.getDate() + daysOffset,
                            );
                            setEndDate(newEndDate);
                          }
                          setStartDate(newValue);
                        }}
                        className="mg-r-10"
                        sx={{ width: "100%", mb: 2 }}
                        disabled={checked}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    xl={3}
                    sm="auto"
                    className="input-date-filter"
                  >
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={es}
                    >
                      <DatePicker
                        label="Fecha de fin"
                        value={endDate}
                        onChange={(newValue) => {
                          if (newValue < startDate) {
                            let newStartDate = new Date(newValue);
                            newStartDate.setDate(
                              newStartDate.getDate() - daysOffset,
                            );
                            setStartDate(newStartDate);
                          }
                          setEndDate(newValue);
                        }}
                        disabled={startDate === null || checked}
                        className="mg-l-10"
                        sx={{ width: "100%", mb: 2 }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              )}
              {isAdmin && (
                <>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    xl={3}
                    sm="auto"
                    className="input-date-filter"
                  >
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={es}
                    >
                      <DatePicker
                        label="Fecha de inicio"
                        value={startDate}
                        onChange={(newValue) => {
                          if (endDate < newValue) {
                            let newEndDate = new Date(newValue);
                            newEndDate.setDate(
                              newEndDate.getDate() + daysOffset,
                            );
                            setEndDate(newEndDate);
                          }
                          setStartDate(newValue);
                        }}
                        className="mg-r-10"
                        sx={{ width: "100%", mb: 2 }}
                        disabled={checked}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    xl={3}
                    sm="auto"
                    className="input-date-filter"
                  >
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={es}
                    >
                      <DatePicker
                        label="Fecha de fin"
                        value={endDate}
                        onChange={(newValue) => {
                          if (newValue < startDate) {
                            let newStartDate = new Date(newValue);
                            newStartDate.setDate(
                              newStartDate.getDate() - daysOffset,
                            );
                            setStartDate(newStartDate);
                          }
                          setEndDate(newValue);
                        }}
                        disabled={startDate === null || checked}
                        className="mg-l-10"
                        sx={{ width: "100%", mb: 2 }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </>
              )}

              {isAdmin && (
                <Grid
                  item
                  xs={12}
                  md={4}
                  xl={2}
                  sm="auto"
                  className="input-service-filter"
                >
                  <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                    <InputLabel htmlFor="select-service-filter">
                      Empresa
                    </InputLabel>
                    <Select
                      value={companyFilter}
                      onChange={(e) => setCompanyFilter(e.target.value)}
                      input={<OutlinedInput id="select-status-filter" />}
                      IconComponent={() => (
                        <i className="material-icons-outlined">expand_more</i>
                      )}
                      disabled={checked}
                    >
                      <MenuItem key={"all"} value={"all"}>
                        Todas
                      </MenuItem>
                      {companies.map(({ id, social_reason }, index) => (
                        <MenuItem key={index} value={id}>
                          {social_reason}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {user?.admin_plan?.plan_type == "admin_checkout" && (
                <Grid
                  item
                  xs={12}
                  md={4}
                  xl={2}
                  sm="auto"
                  className="input-service-filter"
                >
                  <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                    <InputLabel htmlFor="select-service-filte">
                      Conductor Asignado
                    </InputLabel>
                    <Select
                      value={assignedDriverFilter}
                      onChange={(e) => setAssignedDriverFilter(e.target.value)}
                      input={
                        <OutlinedInput id="select-assignedDriverFilter-filter" />
                      }
                      IconComponent={() => (
                        <i className="material-icons-outlined">expand_more</i>
                      )}
                      disabled={checked}
                    >
                      <MenuItem key={"all"} value={"all"}>
                        Todos
                      </MenuItem>
                      <MenuItem key={"assigned"} value={"assigned"}>
                        Tuvo asignado
                      </MenuItem>
                      <MenuItem key={"not_assigned"} value={"not_assigned"}>
                        No tuvo asignado
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

             
              <Grid item xs={12} md={4} xl={2}>
                 <FormControl variant="outlined" sx={{ mb: 0 }}>
                <Button
                  variant="contained"
                  onClick={fetchDeliveries} // Call the fetchDeliveries function when clicked
                  sx={{
                    backgroundColor: "#00c57d",
                    color: "#fff",
                    width: "100%",
                  }}
                >
                  Reload
                </Button>
                 </FormControl>
              </Grid>
             

              <Grid item xs={12} md={6} xl={4}>
                <div>
                  <Tooltip title="Refresco automático de los deliveries cada 1 minuto.">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={checked}
                          onChange={handleToggle}
                          color="primary" // Customize switch color
                          inputProps={{ "aria-label": "controlled" }}
                        />
                      }
                      label={
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          {/* Progress Bar */}
                          {checked && (
                            <div style={{ width: "100%", marginTop: "16px" }}>
                              <LinearProgress
                                variant="determinate"
                                value={
                                  ((progressTimer - progress) / progressTimer) *
                                  100
                                } // Convert seconds to percentage
                              />
                            </div>
                          )}
                          {checked ? "Auto Reload" : "Auto Reload"}
                        </Typography>
                      }
                    />
                  </Tooltip>
                  

                </div>
              </Grid>
            </Grid>
          </Grid>

          {/* Heading: Total Deliveries and Timer */}
          <Grid
            item
            xs={12}
            md={4}
            container
            alignItems="center"
            spacing={2}
          ></Grid>
        </Grid>

        <Grid container sx={{ mb: 4 }}>
          <Typography
            variant="h2" // Increased font size for better visibility
            sx={{ fontWeight: "bold", color: "#000000", fontSize: "3rem" }} // Styled prominently
          >
            {`${deliveriesCount ? deliveriesCount : 0} Deliveries en total`}
          </Typography>
        </Grid>

        {loadingPageData ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "top",
              height: "100vh", // Full viewport height
            }}
          >
            <CircularProgress size={100} />
          </Box>
        ) : (
          <>
            {/* Status Section */}
            <Grid
              container
              spacing={0}
              sx={{ mt: 0 }}
              className="courier-chip-container"
            >
              <Grid item xs={12}>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: "bold", color: "#4caf50", mb: 2 }}
                >
                  Por estado
                </Typography>
              </Grid>
              <Grid container spacing={2} className="chip-btn-container">
                {counterLabels &&
                  counterLabels.map(({ value }, index) => {
                    const count =
                      counters.find((c) => c.value === value)?.count || 0;
                    const formatted = count.toLocaleString("de-DE");

                    const percentage = deliveriesCountResume
                      ? ((count / deliveriesCountResume) * 100).toFixed(2)
                      : 0;

                    return (
                      <Grid item xs={12} sm={6} md={3} lg={2} key={index}>
                        <div
                          style={{
                            border: "1px solid #ccc",
                            borderRadius: "16px",
                            padding: "14px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            minHeight: "150px", // flexible version of your 150px
                            height: "auto",
                          }}
                        >
                          {/* TOP — Counter number */}
                          <Typography
                            variant="h5"
                            component="div"
                            sx={{
                              fontWeight: "bold",
                              textAlign: "center",
                            }}
                          >
                            {formatted}
                          </Typography>

                          {/* MIDDLE — Percentage */}
                          <Typography
                            variant="h6"
                            component="div"
                            sx={{
                              fontWeight: "bold",
                              color: "#4caf50",
                              textAlign: "center",
                              fontSize: {
                                xs: "1rem",
                                md: "1.1rem",
                              },
                            }}
                          >
                            {percentage}%
                          </Typography>

                          {/* BOTTOM — Status name */}
                          <Typography
                            variant="subtitle1"
                            component="div"
                            sx={{
                              fontWeight: "bold",
                              textAlign: "center",
                              mt: "auto", // pushes the label down
                              fontSize: {
                                xs: "1rem",
                                sm: "1rem",
                                md: "1.1rem",
                              },
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              lineHeight: 1.2,
                            }}
                          >
                            {mapStatusDashboard(value)}
                          </Typography>
                        </div>
                      </Grid>
                    );
                  })}
              </Grid>
            </Grid>

            {/* Couriers Section */}
            <Grid
              container
              spacing={2}
              sx={{ mt: 4 }}
              className="courier-chip-container"
            >
              <Grid item xs={12}>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: "bold", color: "#4caf50", mb: 2 }}
                >
                  Por courier
                </Typography>
              </Grid>
              {courierCount &&
                courierCount.map(({ value, count }, index) => {
                  const formattedCount = Number(count).toLocaleString("de-DE");

                  return (
                    <Grid item xs={12} sm={6} md={3} lg={2} key={index}>
                      <div
                        style={{
                          border: "1px solid #ccc",
                          borderRadius: "16px",
                          padding: "14px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                          minHeight: "50px", // flexible version of your 150px
                          height: "auto",
                        }}
                      >
                        {/* Count on top */}
                        <Typography
                          variant="h5"
                          component="div"
                          sx={{
                            fontWeight: "bold",
                            textAlign: "center",
                          }}
                        >
                          {formattedCount}
                        </Typography>

                        {/* Courier name below */}
                        <Typography
                          variant="h6"
                          component="div"
                          sx={{ fontWeight: "bold", textAlign: "center" }}
                        >
                          {value}
                        </Typography>
                      </div>
                    </Grid>
                  );
                })}
            </Grid>

            {/* Orders Without Tracking Number */}
            <Grid
              container
              spacing={2}
              sx={{ mt: 4 }}
              className="courier-chip-container"
            >
              <Grid item xs={12}>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: "bold", color: "#4caf50", mb: 2 }}
                >
                  Pedidos sin tracking number
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <div
                  style={{
                    border: "1px solid #ccc",
                    borderRadius: "16px",
                    padding: "14px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    minHeight: "50px", // flexible version of your 150px
                    height: "auto",
                    backgroundColor:
                      withoutTrackingNumber > 0 ? "#ffcccc" : "#FFFFFF",
                  }}
                >
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ fontWeight: "bold" }}
                  />
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ fontWeight: "bold", textAlign: "center" }}
                  >
                    {withoutTrackingNumber}
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </>
        )}
      </>
    );
  }
}

export default Dashboard;
